// src/components/ProjectList.jsx
//
// "PROJECT - COST ESTIMATION LOG" page (mounted at /project, already
// linked from SideMenu.jsx's "Project" nav item).
//
// Restyled to the CET-HTML-Code theme's own table markup (see
// project.html: nav-pills tabs, .card/.card-header/.table-custom,
// btn-icon action buttons, .pagination.pagination-boxed) instead of the
// page's old hand-rolled page-container/exec-header/project-table
// classes -- those were only ever styled by appLayout.css, which
// Layout.jsx stopped importing once the app moved to the theme's
// compiled CSS. Keeping the old classnames left this whole page
// (title bar, search box, table, pagination) completely unstyled.
//
// Backend: GET /projects (projects_api blueprint in app.py) runs the
// join --
//   SELECT ... FROM projects PP, OpCo OP WHERE PP.opco_id = OP.OpCo_ID
// -- and returns each row's status = 'P' (in progress) / 'C' (completed).
// The theme's two-tab "Estimation Completed / Estimation In Progress"
// split (and the search box, rows-per-page, sort-on-click headers, and
// pagination) are all done client-side against one fetch of every
// project -- there's no ?status= filter on the backend today, and the
// project counts here are small enough (tens, not thousands of rows)
// that fetching once and slicing in React is simpler and safer than
// adding a new backend filter for this restyle. A completed row's Edit
// icon used to be disabled here (and app.py's create_project_replace()
// used to reject the same edit server-side with 403 "project_completed")
// -- per explicit request, completed projects can now be re-edited the
// same as in-progress ones, so that lock was removed on both sides.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { API_ORIGIN, authFetch } from "../config/apiConfig";
import { clearWizardDraft } from "../utils/wizardDraft";
import { canAdd, canEdit, canDelete } from "../utils/permissions";
import { ROUTES } from "../config/routes";
import { printProjectReport } from "../utils/printProjectReport";

const API_BASE = API_ORIGIN;
const ROWS_PER_PAGE_OPTIONS = [5, 8, 10, 15, 20];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const COLUMNS = [
  { key: "projectid", label: "Project ID" },
  { key: "project_name", label: "Project Name" },
  { key: "opco_name", label: "OpCo" },
  { key: "class", label: "Class" },
  { key: "estimation_date", label: "Estimated Date" },
  { key: "estimated_by", label: "Estimated by" },
];

// Parses the "YYYY-MM-DD..." string the backend sends and formats it as
// "20-July-2026" -- done on the raw string (not `new Date(...)`) so a
// browser in a negative UTC-offset timezone can't shift the day back by
// one, which is the classic bug with parsing a plain date via Date().
function formatDate(raw) {
  if (!raw) return "-";
  const m = String(raw).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return raw;
  const [, y, mo, d] = m;
  const monthName = MONTHS[parseInt(mo, 10) - 1] || mo;
  return `${parseInt(d, 10)}-${monthName}-${y}`;
}

function isCompleted(status) {
  return (status || "").trim().toUpperCase() === "C";
}

export default function ProjectList() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Quick Search (Header.jsx) deep-links here as /project?q=<term>&status=<C|P>
  // -- prefill the tab + search box from those on first render so the
  // matched project is actually visible (the tab split below would
  // otherwise hide an in-progress match under the default "completed" tab).
  const [activeTab, setActiveTab] = useState(() =>
    (searchParams.get("status") || "").trim().toUpperCase() === "P" ? "in_progress" : "completed"
  ); // "completed" | "in_progress"
  const [search, setSearch] = useState(() => searchParams.get("q") || "");
  const [rowsPerPage, setRowsPerPage] = useState(8);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState({ key: null, dir: "asc" });

  // Plain React state, same reason as Header.jsx's dropdowns: Bootstrap's
  // own JS (which data-bs-toggle="dropdown" needs) isn't loaded here, so
  // a static `data-bs-toggle` button alone never opens. This also needs
  // the same inline top/right positioning as Header.jsx's menus -- see
  // the comment there -- since `.dropdown-menu` only gets its "below the
  // button" offset from Bootstrap's compiled CSS once Popper.js stamps a
  // `data-bs-popper` attribute onto it, which never happens without
  // Bootstrap's JS.
  const [exportOpen, setExportOpen] = useState(false);
  const exportRef = useRef(null);

  // FIX: "Delete" on a project used to confirm via the browser's own
  // native window.confirm() -- the plain, unstyled "localhost:3000 says..."
  // dialog -- instead of a themed modal like every other delete
  // confirmation in the app (see UsersList.jsx's confirmDeleteUser /
  // confirmDeleteRequest modals for the same teal-card/trash-icon
  // convention, reused below). Holds the row pending deletion, or null
  // when the modal is closed.
  const [confirmDeleteProject, setConfirmDeleteProject] = useState(null);

  // "Proposed" column -- one PDF-icon per row linking to that project's
  // uploaded Proposed Doc (Step1ProjectInfo.jsx's upload field, backed by
  // Project_ProposedDocs / GET /projects/proposed_doc). Keyed by
  // projectid; a missing/undefined entry means "not checked yet or none
  // uploaded" -- rendered as a muted, disabled icon either way. Fetched
  // once per row, in parallel, right after the project list itself loads
  // (there's no bulk "proposed doc status for all projects" endpoint, and
  // the row count here is small -- tens, not thousands -- same tradeoff
  // already made for the client-side search/sort/pagination above).
  const [proposedDocs, setProposedDocs] = useState({});
  const [estimatingRowId, setEstimatingRowId] = useState(null);

  useEffect(() => {
    if (allRows.length === 0) return;
    let cancelled = false;
    Promise.all(
      allRows.map((row) =>
        authFetch(`${API_BASE}/projects/proposed_doc?Projectid=${encodeURIComponent(row.projectid)}`)
          .then((res) => res.json())
          .then((json) => [row.projectid, json && json.success && json.found ? { filename: json.filename } : null])
          .catch(() => [row.projectid, null])
      )
    ).then((entries) => {
      if (cancelled) return;
      const map = {};
      entries.forEach(([projectid, val]) => {
        map[projectid] = val;
      });
      setProposedDocs(map);
    });
    return () => {
      cancelled = true;
    };
  }, [allRows]);

  // Authenticated blob fetch + window.open -- same pattern used throughout
  // the app (BasisOfCostReadOnly.jsx, Step1ProjectInfo.jsx's viewProposedDoc)
  // for any file behind the Authorization header a plain <a href> can't carry.
  // GET /projects/proposed_doc/file requires BOTH Projectid and filename
  // (it looks the row up by that exact pair) -- filename comes from the
  // proposedDocs[] entry the batched status-check effect already fetched,
  // rather than re-deriving it here.
  const viewProposedDoc = (row) => {
    const entry = proposedDocs[row.projectid];
    if (!entry || !entry.filename) {
      alert("No Proposed Doc uploaded for this project.");
      return;
    }
    authFetch(
      `${API_BASE}/projects/proposed_doc/file?Projectid=${encodeURIComponent(row.projectid)}&filename=${encodeURIComponent(entry.filename)}`
    )
      .then((res) => {
        if (!res.ok) throw new Error("Failed to load document");
        return res.blob();
      })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank", "noopener,noreferrer");
      })
      .catch((err) => alert(err.message || "Failed to open Proposed Doc"));
  };

  // "Estimated" column -- generates the same rich, multi-page Cost
  // Estimation Report as EstimationSummary.jsx / DashboardExecutiveSummary.jsx's
  // "Print Report" button, on demand, for an arbitrary row's project (per
  // explicit choice: no server-stored PDF, generate-on-click reusing the
  // existing report-building logic instead).
  const handleEstimatedClick = async (row) => {
    setEstimatingRowId(row.projectid);
    try {
      await printProjectReport({ authFetch, API_BASE, projectId: row.projectid });
    } catch (err) {
      alert(err.message || "Failed to generate report");
    } finally {
      setEstimatingRowId(null);
    }
  };

  useEffect(() => {
    if (!exportOpen) return;
    const handleClickOutside = (e) => {
      if (exportRef.current && !exportRef.current.contains(e.target)) {
        setExportOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [exportOpen]);

  const fetchProjects = useCallback(() => {
    setLoading(true);
    setError("");
    authFetch(`${API_BASE}/projects?page=1&pageSize=1000`)
      .then((res) => res.json())
      .then((json) => {
        if (!json.success) throw new Error(json.error || "Failed to load projects");
        setAllRows(json.data || []);
      })
      .catch((err) => setError(err.message || "Failed to load projects"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // Jump back to page 1 whenever the tab, search text, or page size
  // changes, so results always start at the top instead of landing on
  // a now out-of-range page.
  useEffect(() => {
    setPage(1);
  }, [activeTab, search, rowsPerPage]);

  const tabRows = useMemo(() => {
    const wantCompleted = activeTab === "completed";
    return allRows.filter((r) => isCompleted(r.status) === wantCompleted);
  }, [allRows, activeTab]);

  // Broadened to match /projects?search=' backend's own scope (project
  // name, project ID, OpCo, class, estimated by) -- was project_name /
  // estimated_by only, so a Quick Search hit on a project ID or OpCo name
  // would prefill this search box but then show zero rows here.
  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return tabRows;
    return tabRows.filter(
      (r) =>
        (r.project_name || "").toLowerCase().includes(q) ||
        (r.estimated_by || "").toLowerCase().includes(q) ||
        (r.projectid || "").toString().toLowerCase().includes(q) ||
        (r.opco_name || "").toLowerCase().includes(q) ||
        (r.class || "").toLowerCase().includes(q)
    );
  }, [tabRows, search]);

  const sortedRows = useMemo(() => {
    if (!sort.key) return filteredRows;
    const dir = sort.dir === "asc" ? 1 : -1;
    return [...filteredRows].sort((a, b) => {
      const av = (a[sort.key] ?? "").toString().toLowerCase();
      const bv = (b[sort.key] ?? "").toString().toLowerCase();
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [filteredRows, sort]);

  // FIX: "Export as PDF/CSV/Excel" were plain `<a href="#!">` links with no
  // click handler at all -- the dropdown opened, but choosing any of the
  // three did nothing. Wired up below as three real client-side exporters
  // (no new npm dependency): CSV and the Excel export are a Blob download
  // via a temporary <a download>; PDF opens a print-formatted table in a
  // new tab and calls window.print(), since there's no PDF library
  // installed -- the browser's own "Save as PDF" destination in the print
  // dialog produces the actual file. All three export exactly what's
  // currently on screen: `sortedRows`, i.e. the active tab (Completed /
  // In Progress) and search filter, not just the current page.
  const exportHeaders = COLUMNS.map((c) => c.label);

  const getExportRows = () =>
    sortedRows.map((row) =>
      COLUMNS.map((col) => (col.key === "estimation_date" ? formatDate(row[col.key]) : (row[col.key] ?? "")))
    );

  const getExportFilename = (ext) => {
    const tabLabel = activeTab === "completed" ? "Completed" : "In_Progress";
    const d = new Date();
    const stamp = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}`;
    return `Cost_Estimation_Log_${tabLabel}_${stamp}.${ext}`;
  };

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const csvEscape = (val) => {
    const s = String(val ?? "");
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };

  const handleExportCSV = (e) => {
    e.preventDefault();
    const lines = [exportHeaders.map(csvEscape).join(",")];
    getExportRows().forEach((row) => lines.push(row.map(csvEscape).join(",")));
    // Leading BOM so Excel (not just plain text editors) opens the UTF-8
    // CSV with accented/Arabic OpCo or project names intact instead of
    // guessing the wrong codepage.
    const blob = new Blob(["﻿" + lines.join("\r\n")], { type: "text/csv;charset=utf-8;" });
    downloadBlob(blob, getExportFilename("csv"));
    setExportOpen(false);
  };

  const handleExportExcel = (e) => {
    e.preventDefault();
    const headerHtml = `<tr>${exportHeaders.map((h) => `<th>${h}</th>`).join("")}</tr>`;
    const rowsHtml = getExportRows()
      .map((row) => `<tr>${row.map((cell) => `<td>${String(cell)}</td>`).join("")}</tr>`)
      .join("");
    // The classic dependency-free "Excel export": an HTML table served
    // with the ms-excel MIME type and a .xls extension. Excel opens it as
    // a real spreadsheet -- this is what lets Export as Excel work without
    // pulling in a library like SheetJS.
    const html = `<html><head><meta charset="UTF-8"></head><body><table border="1">${headerHtml}${rowsHtml}</table></body></html>`;
    const blob = new Blob([html], { type: "application/vnd.ms-excel" });
    downloadBlob(blob, getExportFilename("xls"));
    setExportOpen(false);
  };

  const handleExportPDF = (e) => {
    e.preventDefault();
    const headerHtml = `<tr>${exportHeaders.map((h) => `<th>${h}</th>`).join("")}</tr>`;
    const rowsHtml = getExportRows()
      .map((row) => `<tr>${row.map((cell) => `<td>${String(cell)}</td>`).join("")}</tr>`)
      .join("");
    const win = window.open("", "_blank");
    if (!win) {
      alert("Please allow pop-ups for this site to export as PDF.");
      return;
    }
    win.document.write(`
      <html>
        <head>
          <title>${getExportFilename("pdf")}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 24px; color: #212529; }
            h4 { margin-bottom: 16px; }
            table { width: 100%; border-collapse: collapse; }
            th, td { border: 1px solid #ccc; padding: 8px; text-align: left; font-size: 13px; }
            th { background: #f1f1f1; }
            /* FIX, per request: the floating "Print / Save as PDF" button
               that used to sit here is removed -- this export tab is a
               normal page, so the browser's own print/save controls
               (Ctrl+P / Cmd+P, or its menu) already cover the same thing
               without an extra on-page button. There is still no
               auto-triggered window.print() call -- see the comment below
               win.document.close() for why that matters. */
            @media print { .no-print { display: none !important; } }
          </style>
        </head>
        <body>
          <h4>Project Cost Estimation (${activeTab === "completed" ? "Estimation Completed" : "Estimation In Progress"})</h4>
          <table>${headerHtml}${rowsHtml}</table>
        </body>
      </html>
    `);
    win.document.close();
    // FIX: win.focus() used to run here, forcing this export tab to the
    // foreground the instant it opened -- exactly like the Print Report
    // issue on the Dashboard/Estimation Summary pages, this stole the
    // browser's focus away from the main Cost Estimation Tool tab and left
    // it unresponsive to clicks until the user manually switched back.
    // Leaving this out means the export opens quietly in a background tab
    // and the main app keeps working the whole time. There's no floating
    // "Print / Save as PDF" button here either -- printing/saving happens
    // whenever the user chooses, via the browser's own print/save
    // controls (Ctrl+P / Cmd+P, or its menu; see the setTimeout(win.print())
    // this originally replaced).
    setExportOpen(false);
  };

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / rowsPerPage));
  const currentPage = Math.min(page, totalPages);
  const pageRows = sortedRows.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  const toggleSort = (key) => {
    setSort((prev) => {
      if (prev.key !== key) return { key, dir: "asc" };
      return { key, dir: prev.dir === "asc" ? "desc" : "asc" };
    });
  };

  const handleEstimateCost = () => {
    // Was only ever clearing "step1" -- every OTHER step (Budget Amount,
    // Asset Selection, Basis of Cost, the final summary) kept reading
    // whatever the previous estimation had left in localStorage, so a
    // "new" estimation still showed old figures on every page after the
    // first. clearWizardDraft() clears every key the wizard uses across
    // all 7 steps -- see src/utils/wizardDraft.js.
    clearWizardDraft();
    navigate(ROUTES.COST_ESTIMATION_STEP1);
  };

  const handleView = (row) => {
    // FIX: this used to only set "projectID" before navigating to the
    // Executive Summary page. That page independently fetches the
    // project's name from the backend (get_project_estimations_api), so
    // its own breadcrumb was always fine -- but every read-only WIZARD
    // step page (Step1..Step5 View, Basis of Cost View, Element Scope
    // View) shows "Project : {name}" by reading the flat "projectName" /
    // "operatingCompany" localStorage keys (or the "step1" JSON blob for
    // Step1 itself) that only handleEdit below used to populate. Clicking
    // a wizard tab from View mode therefore showed "Project : —" (or a
    // stale name left over from whatever project was last opened/edited)
    // instead of this project's real name, on every one of those pages.
    //
    // Fix: seed the exact same identity keys handleEdit already does,
    // from this same row -- View is read-only everywhere downstream (see
    // each page's disabled <fieldset>), so there's no risk of this being
    // mistaken for actual project data to resave. clearWizardDraft() runs
    // first so nothing from a previously viewed/edited project (old
    // Asset Selection rows, old Basis of Cost draft, etc.) leaks into
    // this project's read-only view.
    clearWizardDraft();

    const step1Data = {
      projectName: row.project_name || "",
      projectID: row.projectid || "",
      estimatedBy: row.estimated_by || "",
      date: row.estimation_date ? String(row.estimation_date).substring(0, 10) : "",
      opco: row.opco_id != null ? String(row.opco_id) : "",
      class: row.class || "",
      C_ID: row.c_id || "",
      duration: row.duration || "",
      intro: row.intro || "",
      inflation: row.inflation || "",
      financial: row.financial || "",
      contingency: row.contingency || "",
      risks: row.risks || "",
      opportunities: row.opportunities || "",
    };
    localStorage.setItem("step1", JSON.stringify(step1Data));
    localStorage.setItem("projectID", row.projectid);
    localStorage.setItem("projectName", row.project_name || "");
    localStorage.setItem("operatingCompany", row.opco_id != null ? String(row.opco_id) : "");
    localStorage.setItem("project_db_id", row.id);
    // FIX, per request ("read only report not showing all data all
    // step"): the "step1" blob above is only ever read by
    // Step1ProjectInfoReadOnly.jsx itself -- Step2RiskInflationReadOnly.jsx
    // /Step5FinalReadOnly.jsx (and Step3/Step4's own C_ID fallback) read
    // these same values back out of FLAT localStorage keys instead
    // ("estimatedBy", "intro", "inflation", "class", "C_ID", ...), the
    // same flat keys the real (editable) Step1ProjectInfo.jsx's own save
    // writes in submitStep1(). Editing a project self-heals this (Step1's
    // own save always runs once before Step2+ can be reached), but View
    // jumps straight to the Estimation Summary / read-only wizard tabs
    // and never touches that save -- so every one of those flat keys was
    // simply never set, and each read-only step downstream silently
    // showed blank fields instead of this project's real data. Seeding
    // them all here, from this same row, closes that gap everywhere at
    // once instead of patching each read-only page separately.
    localStorage.setItem("estimatedBy", row.estimated_by || "");
    localStorage.setItem("estimationDate", step1Data.date);
    localStorage.setItem("intro", row.intro || "");
    localStorage.setItem("inflation", row.inflation || "");
    localStorage.setItem("financial", row.financial || "");
    localStorage.setItem("contingency", row.contingency || "");
    localStorage.setItem("risks", row.risks || "");
    localStorage.setItem("opportunities", row.opportunities || "");
    localStorage.setItem("class", row.class || "");
    localStorage.setItem("C_ID", row.c_id || "");
    navigate(ROUTES.COST_ESTIMATION_SUMMARY);
  };

  const handleEdit = (row) => {
    // Prefill Step1ProjectInfo.jsx the same way it prefills itself after
    // a save -- it reads the "step1" localStorage key on mount.
    const step1Data = {
      projectName: row.project_name || "",
      projectID: row.projectid || "",
      estimatedBy: row.estimated_by || "",
      date: row.estimation_date ? String(row.estimation_date).substring(0, 10) : "",
      opco: row.opco_id != null ? String(row.opco_id) : "",
      class: row.class || "",
      C_ID: row.c_id || "",
      duration: row.duration || "",
      intro: row.intro || "",
      inflation: row.inflation || "",
      financial: row.financial || "",
      contingency: row.contingency || "",
      risks: row.risks || "",
      opportunities: row.opportunities || "",
    };
    localStorage.setItem("step1", JSON.stringify(step1Data));
    localStorage.setItem("projectID", row.projectid);
    localStorage.setItem("project_db_id", row.id);
    navigate(ROUTES.COST_ESTIMATION_STEP1);
  };

  // Opens the themed confirmation modal instead of deleting right away.
  const handleDelete = (row) => {
    setConfirmDeleteProject(row);
  };

  const cancelDeleteProject = () => setConfirmDeleteProject(null);

  // "Delete" on the modal -- same request/error handling the old
  // window.confirm()-gated handleDelete used, just triggered from the
  // modal's button instead of confirm()'s return value.
  const confirmDeleteProjectAction = () => {
    const row = confirmDeleteProject;
    if (!row) return;
    authFetch(`${API_BASE}/projects/${encodeURIComponent(row.projectid)}`, { method: "DELETE" })
      .then((res) => res.json())
      .then((json) => {
        if (!json.success) throw new Error(json.error || "Failed to delete project");
        setConfirmDeleteProject(null);
        fetchProjects();
      })
      .catch((err) => {
        setConfirmDeleteProject(null);
        alert(err.message || "Failed to delete project");
      });
  };

  const sortIcon = (key) => {
    if (sort.key !== key) return <span className="text-muted ms-1">⇅</span>;
    return <span className="ms-1">{sort.dir === "asc" ? "↑" : "↓"}</span>;
  };

  return (
    <>
      <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-between page-title-head gap-3">
        <div>
          {/* Per request, this is now a breadcrumb trail ("Cost
              Estimation / {page}"), same pattern and color (#02bc9c,
              16px) as the Cost Estimation wizard's Step1-7 breadcrumbs
              -- replaces the earlier #788CA5 bordered/uppercase title
              treatment. */}
          <nav aria-label="breadcrumb" className="d-inline-block">
            <span style={{ fontSize: 16, color: "#02bc9c" }}>Cost Estimation</span>
            <span style={{ fontSize: 16, color: "#02bc9c" }} className="mx-2">/</span>
            <span className="fw-semibold" style={{ fontSize: 16, color: "#02bc9c" }}>
              Project Cost Estimation
            </span>
          </nav>
        </div>
        <div className="w-100 w-sm-25 text-start text-md-end">
          {/* View can't create/edit estimations per the Role Matrix (their
              Cost Estimation cell is read-only "View – All estimations") --
              the wizard route itself is already guarded (App.jsx), this
              just keeps the button from being there to click in the first
              place. */}
          {canAdd("CostEstimation") && (
            <button
              type="button"
              // Per request, this button's color is #133c40 (matches the
              // sidebar background) instead of the shared .bg-green utility
              // class's #065f59 -- kept as an inline style + page-scoped
              // change rather than editing .bg-green itself, since that
              // class is also used on AIAssistant.jsx / EmailConfiguration.jsx
              // / RateCard.jsx and shouldn't shift color on those pages too.
              className="btn text-white flex-grow-1 flex-sm-grow-0 text-nowrap"
              style={{ backgroundColor: "#133c40" }}
              onClick={handleEstimateCost}
            >
              <i className="ti ti-plus me-1 fs-sm" /> Cost Estimation
            </button>
          )}
        </div>
      </div>

      <ul className="nav nav-pills bg-light border p-1 mb-3 flex-nowrap flex-sm-wrap overflow-auto" role="tablist">
        <li className="nav-item flex-shrink-0" role="presentation">
          {/* Per request, the active tab's fill is #133c40 (matches the
              sidebar background) instead of the theme's default
              --theme-nav-pills-link-active-bg -- an inline style only
              applied while this tab IS active, rather than overriding
              .nav-pills .nav-link.active globally (that class is shared by
              the tabs on Dashboard.jsx / Notifications.jsx / UsersList.jsx
              / EmailConfiguration.jsx too). */}
          <button
            type="button"
            className={`nav-link fw-semibold px-3 px-md-4${activeTab === "completed" ? " active" : ""}`}
            style={activeTab === "completed" ? { backgroundColor: "#133c40" } : undefined}
            onClick={() => setActiveTab("completed")}
          >
            Estimation Completed
          </button>
        </li>
        <li className="nav-item flex-shrink-0" role="presentation">
          <button
            type="button"
            className={`nav-link fw-semibold px-3 px-md-4${activeTab === "in_progress" ? " active" : ""}`}
            style={activeTab === "in_progress" ? { backgroundColor: "#133c40" } : undefined}
            onClick={() => setActiveTab("in_progress")}
          >
            Estimation In Progress
          </button>
        </li>
      </ul>

      <div className="row">
        <div className="col-xxl-12">
          <div className="card">
            <div className="d-flex align-items-center gap-2 border-light card-header flex-wrap justify-content-between">
              <div className="d-flex gap-2 align-items-center flex-wrap">
                <div className="app-search">
                  <input
                    className="form-control"
                    type="search"
                    placeholder="Search: Project Name, Estimated By"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  <i className="ti app-search-icon text-muted ti-search" />
                </div>
              </div>
              <div className="d-flex align-items-center gap-2">
                <div>
                  <select
                    className="form-control form-select"
                    value={rowsPerPage}
                    onChange={(e) => setRowsPerPage(Number(e.target.value))}
                  >
                    {ROWS_PER_PAGE_OPTIONS.map((n) => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                </div>
                <div className="d-none d-sm-block dropdown" ref={exportRef}>
                  <button
                    className="btn btn-default drop-arrow-none dropdown-toggle"
                    type="button"
                    onClick={() => setExportOpen((open) => !open)}
                    aria-expanded={exportOpen}
                  >
                    <i className="ti me-1 ti-download" /> Export <i className="ti align-middle ms-1 ti-chevron-down" />
                  </button>
                  <ul
                    className={`dropdown-menu dropdown-menu-end${exportOpen ? " show" : ""}`}
                    style={{ position: "absolute", top: "100%", right: 0, left: "auto", marginTop: "0.5rem" }}
                  >
                    <li><a className="dropdown-item" href="#!" onClick={handleExportPDF}>Export as PDF</a></li>
                    <li><a className="dropdown-item" href="#!" onClick={handleExportCSV}>Export as CSV</a></li>
                    <li><a className="dropdown-item" href="#!" onClick={handleExportExcel}>Export as Excel</a></li>
                  </ul>
                </div>
              </div>
            </div>

            {error && (
              <div className="card-body pb-0">
                <div className="alert alert-danger mb-0">{error}</div>
              </div>
            )}

            <div className="table-responsive">
              {/* project-list-table (new, scoped) -- per request, same
                  treatment as RateCard.jsx's "rate-card-table": row
                  background #f2f2f2 @ 38% Transparency, hover #02bc9c @
                  95% Transparency, via themeOverrides.css. Its own class
                  rather than changing .table-custom/.table-hover directly,
                  since those are shared by ManageRoles.jsx / RoleMatrix.jsx
                  / UsersList.jsx / AIAssistant.jsx / EmailConfiguration.jsx
                  and shouldn't change color there too. */}
              <table className="table table-custom table-centered table-hover project-list-table w-100 mb-0">
                <thead className="bg-light bg-opacity-25 thead-sm">
                  <tr className="text-uppercase fs-xxs">
                    {COLUMNS.map((col) => (
                      <th key={col.key} role="button" onClick={() => toggleSort(col.key)}>
                        {col.label} {sortIcon(col.key)}
                      </th>
                    ))}
                    <th className="text-center">Proposed</th>
                    <th className="text-center">Estimated</th>
                    <th className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={COLUMNS.length + 3} className="text-center text-muted py-3">Loading...</td>
                    </tr>
                  ) : pageRows.length === 0 ? (
                    <tr>
                      <td colSpan={COLUMNS.length + 3} className="text-center text-muted py-3">No projects found.</td>
                    </tr>
                  ) : (
                    pageRows.map((row) => {
                      return (
                        <tr key={row.id}>
                          <td>{row.projectid}</td>
                          <td>{row.project_name}</td>
                          <td>{row.opco_name}</td>
                          <td>{row.class}</td>
                          <td>{formatDate(row.estimation_date)}</td>
                          <td>{row.estimated_by}</td>
                          <td className="text-center">
                            {/* Proposed Doc uploaded during "Project Info &
                                Overview" -- muted/disabled when none exists
                                for this project (proposedDocs[projectid] is
                                undefined while the batched fetch is still in
                                flight, or null once it's confirmed absent). */}
                            {proposedDocs[row.projectid] ? (
                              <button
                                type="button"
                                className="btn btn-default btn-icon btn-sm"
                                title={`View Proposed Doc (${proposedDocs[row.projectid].filename})`}
                                onClick={() => viewProposedDoc(row)}
                              >
                                <i className="ti ti-file-type-pdf fs-lg" style={{ color: "#d9534f" }} />
                              </button>
                            ) : (
                              <button
                                type="button"
                                className="btn btn-default btn-icon btn-sm"
                                title="No Proposed Doc uploaded"
                                disabled
                              >
                                <i className="ti ti-file-type-pdf fs-lg text-muted" />
                              </button>
                            )}
                          </td>
                          <td className="text-center">
                            {/* No server-stored PDF -- generated on demand by
                                re-running the same report-building logic the
                                "Print Report" button uses on
                                EstimationSummary.jsx / DashboardExecutiveSummary.jsx. */}
                            <button
                              type="button"
                              className="btn btn-default btn-icon btn-sm"
                              title="Generate & View Estimated Report"
                              disabled={estimatingRowId === row.projectid}
                              onClick={() => handleEstimatedClick(row)}
                            >
                              {estimatingRowId === row.projectid ? (
                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                              ) : (
                                <i className="ti ti-file-type-pdf fs-lg" style={{ color: "#d9534f" }} />
                              )}
                            </button>
                          </td>
                          <td>
                            <div className="d-flex align-items-center justify-content-center gap-1">
                              <button
                                type="button"
                                className="btn btn-default btn-icon btn-sm"
                                title="View"
                                onClick={() => handleView(row)}
                              >
                                <i className="ti ti-eye fs-lg" />
                              </button>
                              {/* Same reasoning as the "Cost Estimation" button
                                  above -- View is read-only, editing opens the
                                  same wizard. */}
                              {canEdit("CostEstimation") && (
                                <button
                                  type="button"
                                  className="btn btn-default btn-icon btn-sm"
                                  title="Edit"
                                  onClick={() => handleEdit(row)}
                                >
                                  <i className="ti ti-edit fs-lg" />
                                </button>
                              )}
                              {/* Admin/Super Admin only per the matrix --
                                  Contributor's Project cell is "Add & edit
                                  own", no delete. */}
                              {canDelete("Project") && (
                                <button
                                  type="button"
                                  className="btn btn-default btn-icon btn-sm"
                                  title="Delete"
                                  onClick={() => handleDelete(row)}
                                >
                                  <i className="ti ti-trash fs-lg" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            <div className="border-0 card-footer">
              <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                <div className="text-muted fs-13">
                  {sortedRows.length === 0
                    ? `Showing 0 of 0 ${activeTab === "completed" ? "completed" : "in progress"}`
                    : `Showing ${(currentPage - 1) * rowsPerPage + 1} to ${Math.min(currentPage * rowsPerPage, sortedRows.length)} of ${sortedRows.length} ${activeTab === "completed" ? "completed" : "in progress"}`}
                </div>
                <ul className="pagination pagination-sm pagination-boxed mb-0 justify-content-center">
                  <li className={`page-item${currentPage === 1 ? " disabled" : ""}`}>
                    <button type="button" className="page-link" onClick={() => setPage((p) => Math.max(1, p - 1))}>
                      <i className="ti ti-chevron-left" />
                    </button>
                  </li>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                    <li key={n} className={`page-item${currentPage === n ? " active" : ""}`}>
                      <button type="button" className="page-link" onClick={() => setPage(n)}>{n}</button>
                    </li>
                  ))}
                  <li className={`page-item${currentPage === totalPages ? " disabled" : ""}`}>
                    <button
                      type="button"
                      className="page-link"
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    >
                      <i className="ti ti-chevron-right" />
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Same teal modal convention as UsersList.jsx's confirmDeleteUser /
          confirmDeleteRequest modals -- replaces the old window.confirm()
          text ("Delete project "X" (Y)? This cannot be undone.") with the
          app's standard delete-confirmation dialog. */}
      {confirmDeleteProject && (
        <div className="modal show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{ backgroundColor: "#124d52" }}>
              <div className="modal-body text-center py-5">
                <div className="bg-white d-inline-flex rounded-circle align-items-center justify-content-center mb-3" style={{ width: 48, height: 48 }}>
                  <i className="ti ti-trash fs-3" style={{ color: "#124d52" }} />
                </div>
                <h5 className="text-white mb-2">
                  Are you sure you want to delete project "{confirmDeleteProject.project_name}" ({confirmDeleteProject.projectid})?
                </h5>
                <p className="text-white-50 mb-0 fs-13">
                  This action cannot be reversed.
                </p>
              </div>
              <div className="modal-footer border-0 justify-content-center pb-4">
                <button type="button" className="btn btn-outline-light px-4" style={{ color: "#fff" }} onClick={cancelDeleteProject}>
                  Cancel
                </button>
                <button type="button" className="btn btn-danger px-4" onClick={confirmDeleteProjectAction}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
